import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Coins, Mail, Lock, User, Building2, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

export default function RegisterPage({ auth }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = auth;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: searchParams.get('role') || 'company',
    company_name: ''
  });

  useEffect(() => {
    if (user) {
      const dashboards = {
        admin: '/admin',
        company: '/company',
        association: '/association',
        ecommerce: '/ecommerce'
      };
      navigate(dashboards[user.role] || '/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur d\'inscription');
      }

      const data = await response.json();
      login(data, data.token);
      toast.success('Inscription réussie !');
      
      const dashboards = {
        admin: '/admin',
        company: '/company',
        association: '/association',
        ecommerce: '/ecommerce',
        employee: '/employee'
      };
      navigate(dashboards[data.role] || '/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/select-role';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const roleLabels = {
    company: 'Entreprise',
    association: 'Association',
    ecommerce: 'E-commerce',
    employee: 'Salarié'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-purple-50 to-pink-50 flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 grain"></div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8 animate-fade-in">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
            <Coins className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            InvesThune
          </span>
        </div>

        <Card className="bg-white/80 backdrop-blur-xl border-purple-100 rounded-2xl shadow-xl animate-slide-up">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Créer un compte
            </CardTitle>
            <CardDescription className="text-slate-600">
              Rejoignez le mouvement InvesThune
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Google Register */}
            <Button
              type="button"
              variant="outline"
              className="w-full mb-6 py-6 border-stone-200 hover:bg-stone-50 rounded-xl"
              onClick={handleGoogleRegister}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              S'inscrire avec Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">ou</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-700">Type de compte</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger 
                    className="py-6 bg-purple-50/50 border-purple-100 rounded-xl"
                    data-testid="role-select"
                  >
                    <SelectValue placeholder="Sélectionnez votre type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Entreprise</SelectItem>
                    <SelectItem value="association">Association</SelectItem>
                    <SelectItem value="ecommerce">E-commerce</SelectItem>
                    <SelectItem value="employee">Salarié</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Nom complet</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jean Dupont"
                    className="pl-10 py-6 bg-purple-50/50 border-purple-100 rounded-xl focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-slate-700">
                  Nom de l'{formData.role === 'association' ? 'association' : formData.role === 'ecommerce' ? 'entreprise' : 'entreprise'}
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="company_name"
                    type="text"
                    placeholder={formData.role === 'association' ? 'Mon Association' : 'Ma Société SAS'}
                    className="pl-10 py-6 bg-purple-50/50 border-purple-100 rounded-xl focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    data-testid="register-company-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    className="pl-10 py-6 bg-purple-50/50 border-purple-100 rounded-xl focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 py-6 bg-purple-50/50 border-purple-100 rounded-xl focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full py-6 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-semibold"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Créer mon compte
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-slate-600 mt-6">
              Déjà un compte ?{' '}
              <Link 
                to="/login" 
                className="text-violet-600 font-medium hover:text-violet-700"
                data-testid="login-link"
              >
                Se connecter
              </Link>
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          <Link to="/" className="hover:text-violet-600">← Retour à l'accueil</Link>
        </p>
      </div>
    </div>
  );
}
